import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { adminApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Blocks, Plus, Search, ChevronUp, ChevronDown, CheckCircle, XCircle, Pencil, Trash2 } from 'lucide-react'
import type { PageComponent } from '@/types/admin'
import { COMPONENT_CATEGORIES } from '@/types/admin'

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      )}
    >
      {isActive ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {isActive ? 'Aktif' : 'Nonaktif'}
    </span>
  )
}

function DataReqBadge({ req }: { req: string | null }) {
  if (!req) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary">
      {req}
    </span>
  )
}

function PlanAccessBadge({ planAccess }: { planAccess: PageComponent['component_plan_access'] }) {
  if (planAccess.length === 0) {
    return <span className="text-xs text-muted-foreground">Semua plan</span>
  }
  return (
    <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
      {planAccess.length} plan
    </span>
  )
}

export default function ComponentsPage() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data: components = [], isLoading } = useQuery({
    queryKey: ['admin', 'components'],
    queryFn: () => adminApi.getComponents(),
  })

  const toggleActive = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      adminApi.updateComponent(id, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'components'] }),
  })

  const moveOrder = useMutation({
    mutationFn: ({ id, sort_order }: { id: string; sort_order: number }) =>
      adminApi.updateComponent(id, { sort_order }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'components'] }),
  })

  const deleteComponent = useMutation({
    mutationFn: (id: string) => adminApi.deleteComponent(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'components'] })
      setDeletingId(null)
    },
  })

  const filtered = components.filter((c) => {
    const matchSearch =
      !search ||
      c.label.toLowerCase().includes(search.toLowerCase()) ||
      c.component_key.toLowerCase().includes(search.toLowerCase())
    const matchCategory = filterCategory === 'all' || c.category === filterCategory
    const matchStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && c.is_active) ||
      (filterStatus === 'inactive' && !c.is_active)
    return matchSearch && matchCategory && matchStatus
  })

  function handleDelete(component: PageComponent) {
    if (deletingId === component.id) {
      deleteComponent.mutate(component.id)
    } else {
      setDeletingId(component.id)
      setTimeout(() => setDeletingId(null), 3000)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Komponen</h1>
          <p className="text-sm text-muted-foreground">
            Kelola katalog komponen yang bisa dipakai tenant untuk kustomisasi halaman publik.
          </p>
        </div>
        <Link
          to="/components/create"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Tambah Komponen
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari label atau component key..."
            className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">Semua Kategori</option>
          {COMPONENT_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">Semua Status</option>
          <option value="active">Aktif</option>
          <option value="inactive">Nonaktif</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Memuat...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center">
            <Blocks className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Tidak ada komponen yang cocok.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Key</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Label</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Kategori</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Data</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Akses Plan</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-28">Urutan</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((comp, idx) => (
                <tr key={comp.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-3">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-muted-foreground">
                      {comp.component_key}
                    </code>
                  </td>
                  <td className="px-4 py-3 font-medium">{comp.label}</td>
                  <td className="px-4 py-3 text-muted-foreground">{comp.category}</td>
                  <td className="px-4 py-3">
                    <DataReqBadge req={comp.data_requirement} />
                  </td>
                  <td className="px-4 py-3">
                    <PlanAccessBadge planAccess={comp.component_plan_access} />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive.mutate({ id: comp.id, is_active: !comp.is_active })}
                      disabled={toggleActive.isPending}
                      className="cursor-pointer disabled:opacity-50"
                    >
                      <StatusBadge isActive={comp.is_active} />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="w-8 text-xs text-muted-foreground tabular-nums">{comp.sort_order}</span>
                      <div className="flex flex-col">
                        <button
                          onClick={() => moveOrder.mutate({ id: comp.id, sort_order: comp.sort_order - 1 })}
                          disabled={idx === 0 || moveOrder.isPending}
                          className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => moveOrder.mutate({ id: comp.id, sort_order: comp.sort_order + 1 })}
                          disabled={idx === filtered.length - 1 || moveOrder.isPending}
                          className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/components/${comp.id}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:border-primary hover:text-primary transition-colors"
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(comp)}
                        disabled={deleteComponent.isPending && deleteComponent.variables === comp.id}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
                          deletingId === comp.id
                            ? 'border-destructive bg-destructive/10 text-destructive'
                            : 'border-border text-muted-foreground hover:border-destructive hover:text-destructive'
                        )}
                        title={deletingId === comp.id ? 'Klik lagi untuk konfirmasi' : 'Hapus'}
                      >
                        <Trash2 className="h-3 w-3" />
                        {deletingId === comp.id ? 'Konfirmasi?' : 'Hapus'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!isLoading && (
        <p className="text-xs text-muted-foreground">
          {filtered.length} dari {components.length} komponen
        </p>
      )}
    </div>
  )
}
